import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Timestamp, collection, doc, onSnapshot } from 'firebase/firestore'
import { ServerClock, indexReplay, replayFrame } from '@racedash/shared'
import type {
  FcyPeriodDoc,
  FuelDoc,
  MessageDoc,
  PitStopDoc,
  ReplayFrame,
  ReplayIndex,
  StandingsDoc,
  StreamSessionDoc,
} from '@racedash/shared'
import { db } from '../lib/firebase'
import { useFieldLapsLive } from './useFieldLaps'
import type { DashboardData } from './useSessionData'

/**
 * Replaying a session on the dashboard, as it happened.
 *
 * Nothing is reinterpreted and nothing is written: the session's stored timing
 * is read once, and every widget is shown it cut to an instant the viewer moves
 * about — playing at a chosen speed, or dragged to a moment. See
 * packages/shared/src/replay.ts for what "as it stood" means for each piece.
 *
 * The cut is shared through a context rather than threaded as props, because
 * the hooks that read for themselves — the field's laps, the event's fuel —
 * sit several screens down and are used by screens that know nothing of
 * replay. Each asks whether a replay of its session is on, and if so reads the
 * frame instead of the live answer; see replayContext.ts.
 */

/** How many times real time a replay may run. */
export const replaySpeeds = [1, 2, 6, 12, 30, 60] as const
export type ReplaySpeed = (typeof replaySpeeds)[number]

/**
 * The session's stored timing, everything a replay is cut from.
 *
 * Listened to rather than read once, so a session still being captured can be
 * replayed too and keeps growing at the far end.
 */
export function useReplaySource(sessionId: string | null): { index: ReplayIndex | null; loading: boolean; error: string | null } {
  const [session, setSession] = useState<StreamSessionDoc | null | undefined>(undefined)
  const [standings, setStandings] = useState<StandingsDoc | null | undefined>(undefined)
  const [pitStops, setPitStops] = useState<PitStopDoc[] | undefined>(undefined)
  const [fcyPeriods, setFcy] = useState<FcyPeriodDoc[] | undefined>(undefined)
  const [messages, setMessages] = useState<MessageDoc[] | undefined>(undefined)
  const [fuel, setFuel] = useState<FuelDoc[] | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const field = useFieldLapsLive(sessionId)

  useEffect(() => {
    setSession(undefined)
    setStandings(undefined)
    setPitStops(undefined)
    setFcy(undefined)
    setMessages(undefined)
    setFuel(undefined)
    setError(null)
    if (!sessionId) return
    const fail = (e: Error) => setError(e.message)
    const all = <T,>(name: string, set: (rows: T[]) => void) =>
      onSnapshot(collection(db, 'streamSessions', sessionId, name), (snap) => set(snap.docs.map((d) => d.data() as T)), fail)
    const unsubs = [
      onSnapshot(doc(db, 'streamSessions', sessionId), (snap) => setSession(snap.exists() ? (snap.data() as StreamSessionDoc) : null), fail),
      onSnapshot(
        doc(db, 'streamSessions', sessionId, 'live', 'standings'),
        (snap) => setStandings(snap.exists() ? (snap.data() as StandingsDoc) : null),
        fail,
      ),
      all<PitStopDoc>('pitStops', setPitStops),
      all<FcyPeriodDoc>('fcyPeriods', setFcy),
      all<MessageDoc>('messages', setMessages),
      all<FuelDoc>('fuel', setFuel),
    ]
    return () => unsubs.forEach((u) => u())
  }, [sessionId])

  const ready =
    session !== undefined &&
    standings !== undefined &&
    pitStops !== undefined &&
    fcyPeriods !== undefined &&
    messages !== undefined &&
    fuel !== undefined &&
    !field.loading
  const index = useMemo(
    () =>
      ready
        ? indexReplay({
            session: session ?? null,
            standings: standings ?? null,
            laps: field.laps,
            pitStops: pitStops!,
            fcyPeriods: fcyPeriods!,
            messages: messages!,
            fuel: fuel!,
          })
        : null,
    [ready, session, standings, field.laps, pitStops, fcyPeriods, messages, fuel],
  )
  return { index, loading: !!sessionId && !ready && !error && !field.error, error: error ?? field.error }
}

/** The frame at `at`, recut at most once a second of replayed time. */
export function useReplayFrame(index: ReplayIndex | null, at: Date | null): ReplayFrame | null {
  const second = at ? Math.floor(at.getTime() / 1000) : null
  return useMemo(
    () => (index && second != null ? replayFrame(index, new Date(second * 1000), Timestamp.fromDate) : null),
    [index, second],
  )
}

/** What the screens are handed during a replay, in place of the live data. */
export function replayData(frame: ReplayFrame, sessionId: string, car: string | null, clock: ServerClock): DashboardData {
  const laps = car ? frame.laps.filter((l) => l.car === car).sort((a, b) => b.lap - a.lap) : []
  return {
    sessionId,
    session: frame.session,
    standings: frame.standings,
    laps,
    messages: frame.messages.slice(0, 40),
    liveMessages: frame.liveMessages,
    stints: frame.stints,
    pitStops: frame.pitStops,
    fcyPeriods: frame.fcyPeriods,
    carSummary: (car && frame.carSummaries.find((s) => s.car === car)) || null,
    fuel: frame.fuel,
    clock,
    // The replayed instant, whatever the widget's own clock says.
    asAt: () => frame.at,
    errors: [],
  }
}

/**
 * The replay's clock: where it is, whether it is running, and how fast.
 *
 * Advanced on a short timer by the real time since the last tick, times the
 * speed, so a slow tab or a dropped tick loses nothing. Stops at the end.
 */
export function useReplayClock(sessionId: string | null, span: { start: Date | null; end: Date | null }) {
  const [at, setAt] = useState<Date | null>(null)
  const [playing, setPlaying] = useState(false)
  const [speed, setSpeed] = useState<ReplaySpeed>(1)
  const startMs = span.start?.getTime() ?? null
  const endMs = span.end?.getTime() ?? null

  // Another session is another race: back to its start, paused.
  const [shown, setShown] = useState(sessionId)
  if (shown !== sessionId) {
    setShown(sessionId)
    setAt(null)
    setPlaying(false)
  }

  // Starts at the green flag once there is one, and stays inside the span as
  // a session still being captured grows.
  useEffect(() => {
    if (startMs == null || endMs == null) return
    setAt((cur) => new Date(cur == null ? startMs : Math.min(Math.max(cur.getTime(), startMs), endMs)))
  }, [startMs, endMs, shown])

  const last = useRef<number | null>(null)
  useEffect(() => {
    if (!playing || endMs == null) {
      last.current = null
      return
    }
    last.current = performance.now()
    const id = setInterval(() => {
      const now = performance.now()
      const step = (now - (last.current ?? now)) * speed
      last.current = now
      setAt((cur) => {
        if (!cur) return cur
        const next = Math.min(cur.getTime() + step, endMs)
        if (next >= endMs) setPlaying(false)
        return new Date(next)
      })
    }, 200)
    return () => clearInterval(id)
  }, [playing, speed, endMs])

  const seek = useCallback(
    (to: number) => {
      if (startMs == null || endMs == null) return
      setAt(new Date(Math.min(Math.max(to, startMs), endMs)))
    },
    [startMs, endMs],
  )

  return { at, playing, setPlaying, speed, setSpeed, seek, start: span.start, end: span.end }
}
